import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface CreateQueueFormData {
  title: string;
  meetingUrl?: string;
  password?: string;
  teamIds?: number[];
}

export interface CreateQueueTeam {
  id: number;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateQueueFormData) => void;
  isPending: boolean;
  teams?: CreateQueueTeam[];
  defaultTeamIds?: number[];
  hasPro?: boolean;
  onUpgrade?: (message: string) => void;
}

export const CreateQueueModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  isPending,
  teams = [],
  defaultTeamIds = [],
  hasPro = false,
  onUpgrade,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<CreateQueueFormData>();
  const [selectedTeamIds, setSelectedTeamIds] = React.useState<number[]>(defaultTeamIds);
  const [showTeamSelector, setShowTeamSelector] = React.useState(defaultTeamIds.length > 0);
  const [autoOpenTeamPopover, setAutoOpenTeamPopover] = React.useState(false);
  const [showEventLink, setShowEventLink] = React.useState(false);
  const [showPasswordField, setShowPasswordField] = React.useState(false);
  const [showPasswordText, setShowPasswordText] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedTeamIds(defaultTeamIds);
      setShowTeamSelector(defaultTeamIds.length > 0);
      setAutoOpenTeamPopover(false);
      setShowEventLink(false);
      setShowPasswordField(false);
      setShowPasswordText(false);
      reset();
    }
  }, [open]);

  const handleClose = () => {
    reset();
    setSelectedTeamIds(defaultTeamIds);
    setShowTeamSelector(defaultTeamIds.length > 0);
    setShowEventLink(false);
    setShowPasswordField(false);
    onClose();
  };

  const handleFormSubmit = (data: CreateQueueFormData) => {
    onSubmit({
      ...data,
      meetingUrl: data.meetingUrl || undefined,
      password: data.password || undefined,
      teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : undefined,
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[480px] p-[24px] flex flex-col gap-[16px]">

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="font-display text-[20px] font-bold text-base-black dark:text-white">
            {t('dashboard.createReviewTitle')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-[#818185] hover:text-base-black dark:text-[#A0A0A3] dark:hover:text-white transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-[12px]">
          {/* Title */}
          <div className="flex flex-col gap-[4px]">
            <input
              placeholder={t('dashboard.reviewTitle')}
              {...register('title', { required: t('dashboard.titleRequired') })}
              autoFocus
              className="h-[44px] px-[16px] text-[16px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
            />
            {errors.title && (
              <p className="font-body text-[12px] text-red-500 ml-[6px]">{errors.title.message}</p>
            )}
          </div>

          {/* Optional pills */}
          <div className="flex flex-col gap-[8px]">
            {/* Team selector */}
            {teams.length > 0 && (
              showTeamSelector ? (
                <div className="flex items-center gap-[8px] flex-wrap">
                  {selectedTeamIds.map((tid) => {
                    const team = teams.find((t) => t.id === tid);
                    return team ? (
                      <span key={tid} className="group inline-flex items-center gap-[6px] px-[12px] py-[6px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] font-body text-[13px] font-semibold text-base-black dark:text-white hover:bg-[#E5E5ED] dark:hover:bg-[#333336] transition-all">
                        {team.name}
                        <button
                          type="button"
                          onClick={() => {
                            const next = selectedTeamIds.filter((id) => id !== tid);
                            setSelectedTeamIds(next);
                            if (next.length === 0) setShowTeamSelector(false);
                          }}
                          className="md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-150 text-[#BFBFC0] hover:text-[#D8210C] flex-shrink-0"
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </span>
                    ) : null;
                  })}
                  <TeamAddPopover
                    teams={teams.filter((t) => !selectedTeamIds.includes(t.id))}
                    onSelect={(id) => { setSelectedTeamIds([...selectedTeamIds, id]); setAutoOpenTeamPopover(false); }}
                    autoOpen={autoOpenTeamPopover}
                  />
                </div>
              ) : (
                <button type="button" onClick={() => {
                  const available = teams.filter((t) => !selectedTeamIds.includes(t.id));
                  if (available.length === 1) {
                    setSelectedTeamIds([...selectedTeamIds, available[0].id]);
                    setShowTeamSelector(true);
                  } else {
                    setShowTeamSelector(true);
                    setAutoOpenTeamPopover(true);
                  }
                }} className="self-start inline-flex items-center gap-[4px] h-[32px] px-[12px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3] font-body text-[12px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] hover:text-[#4A4A4C] dark:hover:text-white transition-all">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  {t('review.addTeam')}
                </button>
              )
            )}
            {/* Event link */}
            {showEventLink ? (
              <div className="flex items-center gap-[8px]">
                <input
                  placeholder={t('review.meetingLinkPlaceholder')}
                  {...register('meetingUrl')}
                  autoFocus
                  className="flex-1 h-[36px] px-[12px] text-[13px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[18px] dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
                />
                <button type="button" onClick={() => { setShowEventLink(false); setValue('meetingUrl', ''); }} className="text-[#818185] hover:text-[#D8210C] transition-colors flex-shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setShowEventLink(true)} className="self-start inline-flex items-center gap-[4px] h-[32px] px-[12px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3] font-body text-[12px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] hover:text-[#4A4A4C] dark:hover:text-white transition-all">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                {t('dashboard.addEventLink')}
              </button>
            )}

            {/* Password — Pro gated */}
            {showPasswordField ? (
              <div className="flex items-center gap-[8px]">
                <input
                  type={showPasswordText ? 'text' : 'password'}
                  placeholder={t('review.passwordPlaceholder')}
                  {...register('password')}
                  autoFocus
                  className="flex-1 h-[36px] px-[12px] text-[13px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[18px] dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
                />
                <button type="button" onClick={() => setShowPasswordText(!showPasswordText)} className="text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {showPasswordText
                      ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>
                      : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                    }
                  </svg>
                </button>
                <button type="button" onClick={() => { setShowPasswordField(false); setValue('password', ''); }} className="text-[#818185] hover:text-[#D8210C] transition-colors flex-shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (!hasPro) { onUpgrade?.(t('review.passwordProRequired')); return; }
                  setShowPasswordField(true);
                }}
                className="self-start inline-flex items-center gap-[4px] h-[32px] px-[12px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3] font-body text-[12px] font-semibold hover:bg-[#E5E5ED] dark:hover:bg-[#333336] hover:text-[#4A4A4C] dark:hover:text-white transition-all"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                {t('dashboard.addPassword')}
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-[8px] pt-[4px]">
            <button
              type="submit"
              disabled={isPending}
              className={`flex-1 h-[44px] rounded-[32px] font-body text-[14px] font-medium text-white transition-all ${isPending ? 'bg-gray-900 opacity-50 cursor-not-allowed' : 'bg-base-black dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'}`}
            >
              {isPending ? t('dashboard.creating') : t('common.create')}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Team Add Popover (shared) ---

const TeamAddPopover = ({ teams, onSelect, autoOpen = false }: { teams: CreateQueueTeam[]; onSelect: (id: number) => void; autoOpen?: boolean }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(autoOpen);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (teams.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-[13px] font-medium px-[12px] py-[6px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
      >
        + {t('review.addTeam')}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-[8px] min-w-[200px] bg-white dark:bg-[#28282B] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[6px] z-20 animate-fadeInMenu">
          {teams.map((team) => (
            <button
              key={team.id}
              type="button"
              onClick={() => { onSelect(team.id); setOpen(false); }}
              className="w-full text-left px-[14px] py-[8px] font-body text-[13px] text-[#111827] dark:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#333336] transition-colors"
            >
              {team.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
